import { SliceCreator } from "@/features/vidyo-call/types/vidyo-common";

type ClosedCaptioningMessage = {
  id: string;
  name: string;
  text: string;
  timestamp: Date;
};

export interface ClosedCaptioningState {
  closedCaptioning: {
    enabled: boolean;
    setEnabled: (enabled: boolean) => void;
    messages: ClosedCaptioningMessage[];
    setMessage: (rawMessage: string) => void;
    subscriptionToken: string | null;
    setSubscriptionToken: (token: string) => void;
  };
}

function parseClosedCaptioningMessage(
  raw: string,
): { type: string; id: string; name: string; text: string } | null {
  // Normalize whitespace and trim
  const normalized = raw.replace(/\s+/g, " ").trim();

  // Try strict match first
  let match = normalized.match(/^\[(\d+)\] \[(\w)\] \[([^\]]+)\] (.+)$/u);

  // If strict match fails, try a more permissive match
  if (!match) {
    match = normalized.match(/^\[(\d+)\]\s*\[(\w)\]\s*\[([^\]]+)\]\s*(.*)$/u);
  }

  if (!match) return null;
  const [, id, type, name, text] = match;
  return { id, type, name, text };
}

export const createClosedCaptioningSlice: SliceCreator<
  keyof ClosedCaptioningState
> = (set) => ({
  closedCaptioning: {
    enabled: false,
    setEnabled: (enabled: boolean) => {
      set(
        (state) => ({
          closedCaptioning: {
            ...state.closedCaptioning,
            enabled,
            // if disabled, clear messages
            messages: enabled ? state.closedCaptioning.messages : [],
          },
        }),
        undefined,
        "closedCaptioning/setEnabled",
      );
    },
    messages: [],
    setMessage: (rawMessage: string) => {
      console.log(`[closedCaptioning/setMessage] ${rawMessage}`);
      set(
        (state) => {
          const parsed = parseClosedCaptioningMessage(rawMessage);
          if (!parsed)
            return { closedCaptioning: { ...state.closedCaptioning } };

          const { id, type, name, text } = parsed;

          if (text.length < 1) {
            return { closedCaptioning: { ...state.closedCaptioning } };
          }

          const messages = [...state.closedCaptioning.messages];

          if (type === "B") {
            // Add new message
            messages.push({ id, name, text, timestamp: new Date() });
          } else if (type === "U" || type === "F") {
            // Update last message with same id
            const idx = messages.map((m) => m.id).lastIndexOf(id);
            if (idx !== -1) {
              messages[idx] = { ...messages[idx], text };
            }
          }

          return {
            closedCaptioning: {
              ...state.closedCaptioning,
              messages,
            },
          };
        },
        undefined,
        "closedCaptioning/setMessages",
      );
    },
    subscriptionToken: null,
    setSubscriptionToken: (token: string) => {
      set(
        (state) => ({
          closedCaptioning: {
            ...state.closedCaptioning,
            subscriptionToken: token,
          },
        }),
        undefined,
        "closedCaptioning/setSubscriptionToken",
      );
    },
  },
});
